import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment as env } from '../../../environments/environment';
import {
  ClientCvTemplate,
  ClientCvTemplateCreatePayload,
  ClientCvTemplateUpdatePayload
} from '../../interfaces/client-cv-template.interface';
import { Paginated } from '../../interfaces/cv';
import { BaseLayout } from '../../interfaces/base-layout.interface';

/**
 * HTTP client for client-uploaded CV background templates.
 *
 * Mirrors CvService conventions:
 *   - environment.api prefix
 *   - withCredentials: true so the Sanctum cookie travels
 *   - Returns Observables for RxJS composition
 */
@Injectable({ providedIn: 'root' })
export class ClientCvTemplateService {

  private readonly httpOptions = { withCredentials: true };

  constructor(private readonly http: HttpClient) {}

  public list(clientId: number, page: number = 1): Observable<Paginated<ClientCvTemplate>> {
    const params = new HttpParams().set('page', page.toString());
    return this.http.get<Paginated<ClientCvTemplate>>(
      `${env.api}/clients/${clientId}/cv/templates`,
      { ...this.httpOptions, params }
    );
  }

  public create(clientId: number, payload: ClientCvTemplateCreatePayload): Observable<ClientCvTemplate> {
    const body = new FormData();
    body.append('name', payload.name);
    body.append('base_layout_id', payload.base_layout_id);
    if (payload.is_active !== undefined) body.append('is_active', payload.is_active ? '1' : '0');
    if (payload.sort_index !== undefined) body.append('sort_index', payload.sort_index.toString());
    body.append('first_page_file', payload.first_page_file);
    if (payload.continuation_file) body.append('continuation_file', payload.continuation_file);

    return this.http.post<ClientCvTemplate>(
      `${env.api}/clients/${clientId}/cv/templates`,
      body,
      this.httpOptions
    );
  }

  public update(
    clientId: number,
    templateId: number,
    payload: ClientCvTemplateUpdatePayload
  ): Observable<ClientCvTemplate> {
    return this.http.put<ClientCvTemplate>(
      `${env.api}/clients/${clientId}/cv/templates/${templateId}`,
      payload,
      this.httpOptions
    );
  }

  public delete(clientId: number, templateId: number, force: boolean = false): Observable<void> {
    let params = new HttpParams();
    if (force) params = params.set('force', '1');
    return this.http.delete<void>(
      `${env.api}/clients/${clientId}/cv/templates/${templateId}`,
      { ...this.httpOptions, params }
    );
  }

  public listBaseLayouts(): Observable<BaseLayout[]> {
    return this.http.get<BaseLayout[]>(`${env.api}/cv/base-layouts`, this.httpOptions);
  }

  public samplePdfUrl(clientId: number, templateId: number): string {
    return `${env.api}/clients/${clientId}/cv/templates/${templateId}/sample-pdf`;
  }

  public sampleImageUrl(clientId: number, templateId: number, updatedAt?: string, baseLayoutId?: string): string {
    // Query params bust the browser cache when template metadata changes.
    // baseLayoutId is included because Eloquent skips UPDATE (and the
    // updated_at bump) when no other attribute is dirty, so a base-layout
    // switch after a no-op autosave would otherwise serve the stale render.
    const params: string[] = [];
    if (updatedAt) params.push(`v=${encodeURIComponent(updatedAt)}`);
    if (baseLayoutId) params.push(`layout=${encodeURIComponent(baseLayoutId)}`);
    const query = params.length ? `?${params.join('&')}` : '';
    return `${env.api}/clients/${clientId}/cv/templates/${templateId}/sample-image${query}`;
  }

  public baseLayoutSamplePdfUrl(layoutId: string): string {
    return `${env.api}/cv/base-layouts/${encodeURIComponent(layoutId)}/sample-pdf`;
  }

  public baseLayoutSampleImageUrl(layoutId: string): string {
    return `${env.api}/cv/base-layouts/${encodeURIComponent(layoutId)}/sample-image`;
  }
}
